// Voice call dock shown in the ChannelSidebar footer while on a call.
// Extracted verbatim from ChatLayout's voiceDock useMemo (step 2.3).
// Structural props only — no imports back into ChatLayout (refactor rule 5);
// prop names mirror ChatLayout/VoiceChannelView for consistency.
//
// The parent decides whether to render this at all (voiceJoined check stays at
// the call site): ChannelSidebar shows its footer wrapper whenever `footer` is
// truthy, and a JSX element is truthy even when the component renders null.
type VoiceDockProps = {
  voiceActiveChannelId: string | null;
  voiceActiveChannelName: string | null;
  voiceActiveSpaceId: string | null;
  currentVoidId: string;
  /** Display name of the call's space — parent resolves it from its spaces list. */
  activeSpaceName: string | null;
  voiceMuted: boolean;
  videoEnabled: boolean;
  videoBusy: boolean;
  devicesOpen: boolean;
  /** Navigation stays parent-side (switchVoid + handleSelectChannel). */
  onJumpToCall: () => void;
  toggleMute: () => void;
  toggleVideo: () => void;
  toggleDevices: () => void;
  refreshDevices: () => void;
  leaveVoice: () => void;
};

export default function VoiceDock({
  voiceActiveChannelId,
  voiceActiveChannelName,
  voiceActiveSpaceId,
  currentVoidId,
  activeSpaceName,
  voiceMuted,
  videoEnabled,
  videoBusy,
  devicesOpen,
  onJumpToCall,
  toggleMute,
  toggleVideo,
  toggleDevices,
  refreshDevices,
  leaveVoice,
}: VoiceDockProps) {
  const btnClass = "h-9 w-9 rounded-lg border border-neutral-800 bg-neutral-900/70 text-fg flex items-center justify-center hover:border-emerald-600/60 disabled:opacity-50";
  const activeLabel = voiceActiveChannelName || (voiceActiveChannelId ? `#${voiceActiveChannelId}` : "Voice call");
  const isInDifferentSpace = !!voiceActiveSpaceId && voiceActiveSpaceId !== currentVoidId;
  return (
    <div className="flex flex-col gap-2 text-xs text-fg-2">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className=" text-meta text-emerald-200">On call</span>
        {isInDifferentSpace && (
          <span className="text-meta text-fg-3 truncate">· {activeSpaceName}</span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="text-meta text-fg-2 truncate">{activeLabel}</div>
        {voiceActiveChannelId && voiceActiveSpaceId && (
          <button
            className="px-2 py-1 rounded border border-emerald-700/50 text-emerald-200 hover:bg-emerald-900/30 text-meta"
            onClick={onJumpToCall}
            title="Jump to call"
            aria-label="Jump to call"
          >
            Open
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          className={`${btnClass} ${voiceMuted ? 'border-red-600/60 text-red-200' : ''}`}
          title={voiceMuted ? "Unmute" : "Mute"}
          aria-label={voiceMuted ? "Unmute" : "Mute"}
          onClick={toggleMute}
        >
          {voiceMuted ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m9 9-5 5v-4a4 4 0 0 1 4-4h.18"/><path d="M15 9.34V5a4 4 0 0 0-7.64-1.12"/><path d="m17 16 5 5"/><path d="m17 11 5-5"/><path d="M15 19a4 4 0 0 1-6.8-2.6L9 15"/><path d="M9 9v6"/></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 18a4 4 0 0 0 4-4V5a4 4 0 1 0-8 0v9a4 4 0 0 0 4 4Z"/><path d="M19 10v2a7 7 0 0 1-7 7"/><path d="M5 10v2a7 7 0 0 0 7 7"/></svg>
          )}
        </button>
        <button
          className={btnClass}
          title={videoEnabled ? "Stop video" : "Start video"}
          aria-label={videoEnabled ? "Stop video" : "Start video"}
          onClick={() => toggleVideo()}
          disabled={videoBusy}
        >
          {videoEnabled ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m17 7 4-4v18l-4-4"/><rect x="3" y="5" width="14" height="14" rx="2" ry="2"/></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M17 10.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4.5l4 4v-11l-4 4Z"/></svg>
          )}
        </button>
        <button
          className={btnClass}
          title={devicesOpen ? "Hide audio devices" : "Show audio devices"}
          aria-label={devicesOpen ? "Hide audio devices" : "Show audio devices"}
          onClick={toggleDevices}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/><path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3"/></svg>
        </button>
        <button
          className={btnClass}
          title="Refresh devices"
          aria-label="Refresh devices"
          onClick={refreshDevices}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
        </button>
        <button
          className="h-9 w-9 rounded-lg border border-red-600/60 bg-red-600/20 text-red-200 flex items-center justify-center hover:bg-red-600/30 hover:border-red-500/80 active:scale-95 disabled:opacity-50"
          title="Leave call"
          aria-label="Leave call"
          onClick={leaveVoice}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 rotate-[135deg]"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 5.15 12.8 19.79 19.79 0 0 1 2.08 4.16 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.89.33 1.77.62 2.61a2 2 0 0 1-.45 2.11L8 9.66a16 16 0 0 0 6.34 6.34l1.22-1.23a2 2 0 0 1 2.11-.45c.84.29 1.72.5 2.61.62A2 2 0 0 1 22 16.92z"/></svg>
        </button>
      </div>
    </div>
  );
}
